// src/admin/sections/SettingsSection.jsx
//
// Ticket 49 -- Admin: Settings, the rules in force. A read-only card, not a
// form. None of these is a stored setting, and a control that changes nothing
// is worse than no control.
//
// EVERY NUMBER ON THIS SCREEN IS READ FROM WHERE IT LIVES, NEVER TYPED AGAIN:
//
//   craving minimum      imported: MIN_CRAVING_WORDS   (src/lib/cravingWords.js)
//   similarity threshold imported: SIMILARITY_THRESHOLD (src/lib/matchThreshold.js)
//   popular / gem line   imported: POPULAR_LINE_TEXT (src/lib/popularity.js)
//   gems per anchor      asked of the database: pipeline_cap_per_anchor()
//   author description floor, and the Overheard table's own minimum
//                        read back out of their CHECK constraints by
//                        admin_rules_in_force() (20261020200000_ticket49_rules_in_force.sql)
//
// Change SIMILARITY_THRESHOLD on a branch and this screen shows the new value
// without anyone touching it. scripts/check-settings-rules.mjs fails the build
// if a number is ever typed into this file instead.
//
// A number that cannot be read is shown as "can't read it", never as 0 and
// never as the last value anyone remembers. The reader entry minimums (the
// tropes and tags a reader must supply, and the genre) are product rules, not
// discovery dials, and are not on this screen on purpose.

import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { MIN_CRAVING_WORDS } from '../../lib/cravingWords';
import { SIMILARITY_THRESHOLD } from '../../lib/matchThreshold';
import { POPULAR_LINE_TEXT } from '../../lib/popularity';

const isCount = (n) => typeof n === 'number' && Number.isInteger(n) && n >= 0;

// The three numbers read from the database. Each resolves to { ok: true, value } or { ok: false, why }.
// Each read keeps its own { data, error }, so a failed one is told apart from a
// zero and does not take the others down with it.
async function readFloors() {
  const { data, error } = await supabase.rpc('admin_rules_in_force');
  return { data, error };
}
async function readCap() {
  const { data, error } = await supabase.rpc('pipeline_cap_per_anchor');
  return { data, error };
}

export async function loadRulesInForce() {
  const [floors, cap] = await Promise.all([readFloors(), readCap()]);

  const { data: floorRows, error: floorErr } = floors;
  let descriptionFloor, overheardFloor;
  if (floorErr) {
    // 42883 / PGRST202: the function has not been created yet (ticket 49's SQL).
    const missing = floorErr.code === '42883' || floorErr.code === 'PGRST202';
    const why = missing
      ? "The database function that reads this has not been added yet (the SQL for this ticket)."
      : `The database would not say: ${floorErr.message}`;
    descriptionFloor = { ok: false, why };
    overheardFloor   = { ok: false, why };
  } else {
    const row = Array.isArray(floorRows) ? floorRows[0] : floorRows;
    const unreadable = "The rule is not in the database as one clear minimum, so this card will not guess it.";
    descriptionFloor = isCount(row?.description_floor) ? { ok: true, value: row.description_floor } : { ok: false, why: unreadable };
    overheardFloor   = isCount(row?.overheard_floor)   ? { ok: true, value: row.overheard_floor }   : { ok: false, why: unreadable };
  }

  const { data: capValue, error: capErr } = cap;
  const gemsPerAnchor = capErr
    ? { ok: false, why: `The database would not say: ${capErr.message}` }
    : isCount(capValue) ? { ok: true, value: capValue } : { ok: false, why: 'The database gave an answer this card could not read as a number.' };

  return { descriptionFloor, overheardFloor, gemsPerAnchor };
}

const words = (n) => `${n} ${n === 1 ? 'word' : 'words'}`;

// The rows. `value` is a function of what was read, so no number is written
// into this list. `wired: false` rows read nothing: they say what the rule will
// be and what it is waiting on.
export function buildRules(read) {
  const fromDb = (r, fmt) => (!r ? { state: 'loading' } : r.ok ? { state: 'value', text: fmt(r.value) } : { state: 'unreadable', why: r.why });

  return [
    {
      id: 'description-floor',
      name: 'Author description floor',
      value: fromDb(read?.descriptionFloor, words),
      does: "The fewest words an author's description can have before the book saves. Since the blurb was removed this is the only prose an author writes, so a thin one makes a weak search vector and thin card copy.",
      breaks: "Too low and thin descriptions get in. Too high and authors cannot finish a book. The author app's add-book and claim forms each count words on their own, so both forms and the database have to change together, or an author is told yes and then refused.",
      lives: "The database: books_description_word_floor, which applies to books submitted by authors (claims included). Changing it needs a migration, and the same number in both places the author app checks it before the database does: the add-book form (DESCRIPTION_MIN_WORDS in AddBook.jsx) and the claim form (CLAIM_DESCRIPTION_MIN_WORDS in MyBooks.jsx).",
      wired: true,
    },
    {
      id: 'craving-minimum',
      name: 'Craving minimum',
      value: { state: 'value', text: words(MIN_CRAVING_WORDS) },
      agreement: read?.overheardFloor || null,
      does: "The words a reader must write before the search runs. The examples under the craving box (Overheard) are held to the same minimum, so they never teach a reader to write something the box refuses.",
      breaks: "Too low and searches run on too little to match. Too high and readers give up. If the code and the Overheard table ever disagree, an example can be one the box itself would refuse.",
      lives: "Code: MIN_CRAVING_WORDS in src/lib/cravingWords.js. The Overheard table enforces its own copy in the database (overheard_items_min_words), so changing the minimum means changing both.",
      wired: true,
    },
    {
      id: 'similarity-threshold',
      name: 'Similarity threshold',
      value: { state: 'value', text: String(SIMILARITY_THRESHOLD) },
      does: "Where a result counts as a real match. At or above it the deck labels a book \"X% Match\"; below it, \"Closest: X%\". The Popular to Hidden Gem strip shows only when at least one pairing clears it. Below it the homepage treats the craving as a dead end and offers the journal on the closest book, and the Cravings log marks it \"below the bar\".",
      breaks: "Too low and weak matches are labelled as matches. Too high and more readers get the dead-end offer, and \"closest\" shows on books that do fit. The code calls it a starting guess to be revisited against the real spread of best matches in Cravings.",
      lives: "Code: SIMILARITY_THRESHOLD in src/lib/matchThreshold.js (ticket 27). This card imports it. It is never copied into a second constant.",
      wired: true,
    },
    {
      id: 'popular-gem-line',
      name: 'Popular / gem line',
      value: { state: 'value', text: POPULAR_LINE_TEXT },
      does: "Where a book counts as popular and where it counts as a hidden gem. A book with neither reading is Unknown, not a gem. It only sorts the well-known anchor from the gem in a pipeline; it never makes a book recommendable and never raises its rank.",
      breaks: "Too high and well-known books count as gems. Too low and real gems are held back as popular.",
      lives: "Code: POPULAR_EDITIONS and POPULAR_RATINGS in src/lib/popularity.js (ticket 39). Books, Pipelines and this card all read it from there, and the counts on Books are handed the same two numbers.",
      wired: true,
    },
    {
      id: 'gems-per-anchor',
      name: 'Gems per anchor',
      value: fromDb(read?.gemsPerAnchor, (n) => String(n)),
      does: "How many Hidden Gems can surface under one well-known book.",
      breaks: "Too high and one popular book crowds the strip. Too low and good gems are held back.",
      lives: "The database: pipeline_cap_per_anchor() (ticket 26 part B). Changing it needs a migration.",
      wired: true,
    },
    {
      id: 'reader-signal',
      name: 'Readers behind a pairing, and strength of the reader signal',
      value: { state: 'notwired' },
      does: "How many separate readers must pair the same two books before the pairing counts, and how hard reader pairings nudge the order of results.",
      breaks: "Too few and one person can push a pairing through (the troll guard). Too strong and reader pairings drown out the match.",
      lives: "Not built yet.",
      waitingOn: 'Ticket 28',
      wired: false,
    },
  ];
}

// Does the Overheard table's own minimum agree with the code's?
// Returns { kind: 'agree' | 'disagree' | 'unknown', text }.
export function overheardAgreement(overheardFloor) {
  if (!overheardFloor) return { kind: 'unknown', text: 'Checking the Overheard table…' };
  if (!overheardFloor.ok) return { kind: 'unknown', text: `The Overheard table's own minimum could not be read. ${overheardFloor.why}` };
  if (overheardFloor.value === MIN_CRAVING_WORDS) return { kind: 'agree', text: `The Overheard table enforces ${words(overheardFloor.value)} as well. They agree.` };
  return { kind: 'disagree', text: `The code says ${words(MIN_CRAVING_WORDS)} but the Overheard table enforces ${words(overheardFloor.value)}. They must match.` };
}

const label = { fontSize: 'var(--font-size-xs)', fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase', color: 'var(--neutral-400)', marginBottom: 2 };
const body  = { fontSize: 'var(--font-size-sm)', color: 'var(--neutral-600)', lineHeight: 1.5, margin: 0 };

function Pill({ wired, waitingOn }) {
  return wired
    ? <span style={{ fontSize: 'var(--font-size-tiny)', fontWeight: 700, letterSpacing: 0.5, padding: '2px 6px', borderRadius: 'var(--radius-md)', background: 'var(--feedback-success-bg)', color: 'var(--neutral-700)', border: '1px solid var(--neutral-200)', whiteSpace: 'nowrap' }}>IN FORCE</span>
    : <span style={{ fontSize: 'var(--font-size-tiny)', fontWeight: 700, letterSpacing: 0.5, padding: '2px 6px', borderRadius: 'var(--radius-md)', border: '1px solid var(--feedback-warning-border)', color: 'var(--feedback-warning-ink)', background: 'var(--brand-white)', whiteSpace: 'nowrap' }}>NOT WIRED{waitingOn ? ` · WAITS FOR ${waitingOn.toUpperCase()}` : ''}</span>;
}

function Value({ value }) {
  if (value.state === 'value')     return <span style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 700, color: 'var(--neutral-800)', letterSpacing: -0.5 }}>{value.text}</span>;
  if (value.state === 'loading')   return <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-400)' }}>Reading…</span>;
  if (value.state === 'notwired')  return <span style={{ fontSize: 'var(--font-size-base)', color: 'var(--neutral-400)' }}>No value yet</span>;
  return <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--feedback-warning-ink)' }}>Can't read it</span>;
}

function RuleCard({ rule, isMobile }) {
  const agree = rule.agreement !== undefined ? overheardAgreement(rule.agreement) : null;
  return (
    <section aria-label={rule.name} data-rule={rule.id} style={{ background: 'var(--brand-white)', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-lg)', padding: isMobile ? '14px 12px' : '18px 20px', marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
        <div style={{ minWidth: 0 }}>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--font-size-md)', fontWeight: 700, color: 'var(--neutral-800)', margin: 0 }}>{rule.name}</h2>
          <div style={{ marginTop: 4 }}><Value value={rule.value} /></div>
        </div>
        <Pill wired={rule.wired} waitingOn={rule.waitingOn} />
      </div>

      {rule.value.state === 'unreadable' && (
        <p role="status" style={{ ...body, color: 'var(--feedback-warning-ink)', background: 'var(--feedback-warning-bg)', border: '1px solid var(--feedback-warning-border)', borderRadius: 'var(--radius-md)', padding: '8px 10px', marginBottom: 10 }}>{rule.value.why}</p>
      )}
      {agree && (
        <p role="status" data-agreement={agree.kind} style={{ ...body, marginBottom: 10, fontWeight: agree.kind === 'disagree' ? 700 : 400, color: agree.kind === 'disagree' ? 'var(--feedback-warning-ink)' : 'var(--neutral-600)' }}>{agree.text}</p>
      )}

      <div style={{ display: 'grid', gap: 10 }}>
        <div><div style={label}>What it does</div><p style={body}>{rule.does}</p></div>
        <div><div style={label}>If it is wrong</div><p style={body}>{rule.breaks}</p></div>
        <div><div style={label}>Where it lives</div><p style={body}>{rule.lives}</p></div>
      </div>
    </section>
  );
}

export default function SettingsSection({ isMobile }) {
  const [read, setRead] = useState(null);

  useEffect(() => {
    let live = true;
    loadRulesInForce().then(r => { if (live) setRead(r); });
    return () => { live = false; };
  }, []);

  const rules = buildRules(read);

  return (
    <div style={{ padding: isMobile ? 16 : '30px 36px', fontFamily: 'var(--font-body)', maxWidth: 860 }}>
      <div style={{ marginBottom: 16 }}>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 26, fontWeight: 700, color: 'var(--neutral-800)', letterSpacing: -0.6, lineHeight: 1.1, margin: 0 }}>Settings</h1>
        <p style={{ fontSize: 'var(--font-size-base)', color: 'var(--neutral-500)', marginTop: 4 }}>
          The rules the product is running on right now. Read only: none of these is a stored setting, so there is nothing here to change. Each value is read from where it lives.
        </p>
      </div>

      {rules.map(r => <RuleCard key={r.id} rule={r} isMobile={isMobile} />)}

      <p style={{ ...body, color: 'var(--neutral-500)', marginTop: 4 }}>
        Not on this card, on purpose: what a reader must supply when adding a book (the tropes and tags, and the genre). Those are product rules about what a book needs to be findable, not discovery dials, so they have no control and no place here.
      </p>
    </div>
  );
}
